import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/use-auth';
import { paths } from '@/routes/paths';
import { useColorScheme } from '@/theme';

import { SettingsCard } from '../components/settings-card';
import { SettingsRow } from '../components/settings-row';
import {
  useDeleteAccount,
  useDeleteAllConversations,
  useExportData,
} from '../hooks/use-privacy-actions';
import { useSaveHistoryPref } from '../hooks/use-save-history-pref';

function SectionHeading({ title }: { title: string }) {
  return (
    <Typography
      sx={{
        fontFamily: '"Cairo Variable", sans-serif',
        fontSize: 22,
        fontWeight: 700,
        color: 'text.primary',
        mb: 3,
        letterSpacing: '-0.01em',
        display: { xs: 'none', md: 'block' },
      }}
    >
      {title}
    </Typography>
  );
}

function ReadOnlyValue({ value }: { value: string }) {
  return <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{value}</Typography>;
}

function LinkButton({
  label,
  danger = false,
  onClick,
}: {
  label: string;
  danger?: boolean;
  onClick?: () => void;
}) {
  return (
    <Button
      size="small"
      onClick={onClick}
      sx={(theme) => ({
        color: danger ? theme.palette.error.main : theme.palette.primary.main,
        fontSize: 12.5,
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: (t) => `${t.shape.borderRadius}px`,
      })}
    >
      {label}
    </Button>
  );
}

function OutlineButton({
  label,
  onClick,
  disabled = false,
}: {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={onClick}
      disabled={disabled}
      sx={{
        borderColor: 'divider',
        color: 'text.primary',
        borderRadius: (t) => `${t.shape.borderRadius}px`,
        textTransform: 'none',
        '&:hover': { borderColor: 'text.primary', backgroundColor: 'action.hover' },
      }}
    >
      {label}
    </Button>
  );
}

interface SectionProps {
  isMobile?: boolean;
}

interface AccountSectionProps extends SectionProps {
  onChangePassword?: () => void;
  onManageSessions?: () => void;
}

export function AccountSection({
  onChangePassword,
  onManageSessions,
  isMobile = false,
}: AccountSectionProps = {}) {
  const { t, i18n } = useTranslation('settings');
  const { user } = useAuth();

  const displayName = user?.displayName || t('account.fallbackName');
  const email = user?.email ?? '';
  const initials = getInitials(displayName);
  const joined = formatJoinedLabel(
    user?.createdAt,
    i18n.language,
    t('account.joinedPrefix'),
    t('account.joinedUnknown'),
  );
  // `authMethods` is only set if the backend response includes it. When absent
  // (older sessions, refreshed before re-login), default to allowing password
  // change — the backend still enforces the actual auth method check.
  const hasPasswordAuth = !user?.authMethods || user.authMethods.includes('email');
  const emailVerified = user?.isEmailVerified !== false;

  return (
    <Box sx={{ maxWidth: 720 }}>
      {/* Hidden on mobile — the drill-down header already shows the title. */}
      {!isMobile && <SectionHeading title={t('account.title')} />}
      <SettingsCard>
        <Stack direction="row" alignItems="center" spacing={2.5} sx={{ py: 2.5 }}>
          <Box
            aria-hidden
            sx={(theme) => ({
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: `${theme.palette.primary.main}33`,
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '"Cairo Variable", sans-serif',
              fontWeight: 700,
              fontSize: 22,
              overflow: 'hidden',
              backgroundImage: user?.avatarUrl ? `url(${user.avatarUrl})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            })}
          >
            {!user?.avatarUrl && initials}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>
              {displayName}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {[email, joined].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Stack>
      </SettingsCard>
      <SettingsCard title={t('account.profile.title')}>
        <SettingsRow
          label={t('account.profile.displayName.label')}
          hint={t('account.profile.displayName.hint')}
          control={<ReadOnlyValue value={displayName} />}
        />
        <SettingsRow
          label={t('account.profile.email.label')}
          hint={t('account.profile.email.hint')}
          control={
            <Stack direction="row" alignItems="center" spacing={1}>
              <ReadOnlyValue value={email || '—'} />
              {!emailVerified && (
                <Typography
                  component="span"
                  sx={(theme) => ({
                    fontSize: 11,
                    fontWeight: 600,
                    color: theme.palette.error.main,
                    paddingInline: 0.75,
                    paddingBlock: 0.25,
                    borderRadius: (t) => `${t.shape.borderRadius}px`,
                    backgroundColor: `${theme.palette.error.main}1F`,
                  })}
                >
                  {t('account.profile.email.unverified')}
                </Typography>
              )}
            </Stack>
          }
        />
        <SettingsRow
          label={t('account.profile.password.label')}
          hint={
            hasPasswordAuth
              ? t('account.profile.password.hint')
              : t('account.profile.password.hintOAuth')
          }
          control={
            hasPasswordAuth && onChangePassword ? (
              <LinkButton label={t('account.profile.password.action')} onClick={onChangePassword} />
            ) : (
              <ReadOnlyValue value="—" />
            )
          }
        />
        <SettingsRow
          label={t('account.sessions.rowLabel')}
          hint={t('account.sessions.rowHint')}
          control={
            onManageSessions ? (
              <LinkButton label={t('account.sessions.rowAction')} onClick={onManageSessions} />
            ) : (
              <ReadOnlyValue value="—" />
            )
          }
        />
      </SettingsCard>
    </Box>
  );
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatJoinedLabel(
  iso: string | undefined,
  locale: string,
  prefix: string,
  unknownLabel: string,
): string {
  if (!iso) return unknownLabel;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return unknownLabel;
  const formatted = date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  return `${prefix}${formatted}`;
}

export function PreferencesSection({ isMobile = false }: SectionProps = {}) {
  const { t, i18n } = useTranslation('settings');
  const { mode, setMode } = useColorScheme();

  const currentLang: 'en' | 'ar' = i18n.language.startsWith('ar') ? 'ar' : 'en';

  return (
    <Box sx={{ maxWidth: 720 }}>
      {!isMobile && <SectionHeading title={t('preferences.title')} />}
      <SettingsCard title={t('preferences.appearance.title')}>
        <SettingsRow
          label={t('preferences.appearance.dark.label')}
          hint={t('preferences.appearance.dark.hint')}
          control={
            <Switch
              checked={mode === 'dark'}
              onChange={(_e, checked) => setMode(checked ? 'dark' : 'light')}
              inputProps={{ 'aria-label': t('preferences.appearance.dark.label') }}
            />
          }
        />
      </SettingsCard>
      <SettingsCard title={t('preferences.language.title')}>
        <SettingsRow
          label={t('preferences.language.interface.label')}
          hint={t('preferences.language.interface.hint')}
          control={
            <TextField
              select
              size="small"
              value={currentLang}
              onChange={(e) => {
                void i18n.changeLanguage(e.target.value);
              }}
              sx={{ minWidth: 160, '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
            >
              <MenuItem value="en" sx={{ fontSize: 13 }}>
                {t('preferences.language.interface.en')}
              </MenuItem>
              <MenuItem value="ar" sx={{ fontSize: 13 }}>
                {t('preferences.language.interface.ar')}
              </MenuItem>
            </TextField>
          }
        />
      </SettingsCard>
    </Box>
  );
}

export function NotificationsSection({ isMobile = false }: SectionProps = {}) {
  const { t } = useTranslation('settings');
  return (
    <Box sx={{ maxWidth: 720 }}>
      {!isMobile && <SectionHeading title={t('notifications.title')} />}
      <SettingsCard title={t('notifications.email.title')}>
        <SettingsRow
          label={t('notifications.email.product.label')}
          hint={t('notifications.email.product.hint')}
          control={<Switch />}
        />
      </SettingsCard>
    </Box>
  );
}

type Toast = { severity: 'success' | 'error'; messageKey: string } | null;
type Confirm = 'chats' | 'account' | null;

export function PrivacyDataSection({ isMobile = false }: SectionProps = {}) {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { saveHistory, setSaveHistory } = useSaveHistoryPref();

  const exportMut = useExportData();
  const deleteAllMut = useDeleteAllConversations();
  const deleteAccountMut = useDeleteAccount();

  const [confirm, setConfirm] = useState<Confirm>(null);
  const [toast, setToast] = useState<Toast>(null);

  const handleExport = () => {
    exportMut.mutate(undefined, {
      onSuccess: () =>
        setToast({ severity: 'success', messageKey: 'privacy.feedback.exportSuccess' }),
      onError: () => setToast({ severity: 'error', messageKey: 'privacy.feedback.exportError' }),
    });
  };

  const handleDeleteChats = () => {
    deleteAllMut.mutate(undefined, {
      onSuccess: () => {
        setConfirm(null);
        setToast({ severity: 'success', messageKey: 'privacy.feedback.deleteChatsSuccess' });
      },
      onError: () =>
        setToast({ severity: 'error', messageKey: 'privacy.feedback.deleteChatsError' }),
    });
  };

  const handleDeleteAccount = () => {
    deleteAccountMut.mutate(undefined, {
      onSuccess: async () => {
        setConfirm(null);
        await logout();
        navigate(paths.home, { replace: true });
      },
      onError: () =>
        setToast({ severity: 'error', messageKey: 'privacy.feedback.deleteAccountError' }),
    });
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      {!isMobile && <SectionHeading title={t('privacy.title')} />}
      <SettingsCard title={t('privacy.conversations.title')}>
        <SettingsRow
          label={t('privacy.conversations.save.label')}
          hint={t('privacy.conversations.save.hint')}
          control={
            <Switch
              checked={saveHistory}
              onChange={(_e, checked) => setSaveHistory(checked)}
              inputProps={{ 'aria-label': t('privacy.conversations.save.label') }}
            />
          }
        />
      </SettingsCard>
      <SettingsCard title={t('privacy.data.title')}>
        <SettingsRow
          label={t('privacy.data.export.label')}
          hint={t('privacy.data.export.hint')}
          control={
            <OutlineButton
              label={
                exportMut.isPending
                  ? t('privacy.data.export.actionPending')
                  : t('privacy.data.export.action')
              }
              onClick={handleExport}
              disabled={exportMut.isPending}
            />
          }
        />
        <SettingsRow
          label={t('privacy.data.deleteChats.label')}
          hint={t('privacy.data.deleteChats.hint')}
          control={
            <LinkButton
              label={t('privacy.data.deleteChats.action')}
              danger
              onClick={() => setConfirm('chats')}
            />
          }
        />
        <SettingsRow
          label={t('privacy.data.deleteAccount.label')}
          hint={t('privacy.data.deleteAccount.hint')}
          control={
            <LinkButton
              label={t('privacy.data.deleteAccount.action')}
              danger
              onClick={() => setConfirm('account')}
            />
          }
        />
      </SettingsCard>

      <Dialog open={confirm === 'chats'} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('privacy.confirmDeleteChats.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('privacy.confirmDeleteChats.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirm(null)}
            disabled={deleteAllMut.isPending}
            sx={{ borderRadius: (t) => `${t.shape.borderRadius}px` }}
          >
            {t('privacy.confirmDeleteChats.cancel')}
          </Button>
          <Button
            onClick={handleDeleteChats}
            disabled={deleteAllMut.isPending}
            sx={{
              color: 'error.main',
              fontWeight: 600,
              borderRadius: (t) => `${t.shape.borderRadius}px`,
            }}
          >
            {t('privacy.confirmDeleteChats.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirm === 'account'} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('privacy.confirmDeleteAccount.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('privacy.confirmDeleteAccount.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirm(null)}
            disabled={deleteAccountMut.isPending}
            sx={{ borderRadius: (t) => `${t.shape.borderRadius}px` }}
          >
            {t('privacy.confirmDeleteAccount.cancel')}
          </Button>
          <Button
            onClick={handleDeleteAccount}
            disabled={deleteAccountMut.isPending}
            sx={{
              color: 'error.main',
              fontWeight: 600,
              borderRadius: (t) => `${t.shape.borderRadius}px`,
            }}
          >
            {t('privacy.confirmDeleteAccount.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {t(toast.messageKey)}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
